import { useState, useRef, useEffect } from 'react';
import IconClose from '@hiredigital/ui/Icon/icons/close.inline.svg';
import IconSend from '@hiredigital/ui/Icon/icons/right-arrow.inline.svg';
import { useUser } from '@context/user';
import Styles from './SupportWidget.module.scss';

const WEBHOOK_URL = process.env.SLACK_SUPPORT_WEBHOOK_URL;
const AUTO_REPLY = `Thanks for reaching out! We've received your message and will get back to you as soon as possible.`;

const SupportModal = ({ isOpen, onToggle }) => {
  const user = useUser();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const hasReplied = useRef(false);

  useEffect(() => {
    if (isOpen) {
      textareaRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isSending) return;

    setInput('');
    setMessages((prev) => [...prev, { from: 'user', text }]);
    setIsSending(true);

    try {
      const slackText = `*New support message*\n*From:* ${user?.name || 'Unknown'} (${user?.email || 'no email'})\n*Page:* ${window.location.href}\n\n> ${text}`;
      await fetch(WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors',
        body: JSON.stringify({ text: slackText }),
      });
    } catch (_) {
      // silent — message display is optimistic
    } finally {
      setIsSending(false);
    }

    if (!hasReplied.current) {
      hasReplied.current = true;
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setMessages((prev) => [...prev, { from: 'support', text: AUTO_REPLY }]);
      }, 1500);
    }
  };

  const handleClose = () => {
    setMessages([]);
    setInput('');
    setIsTyping(false);
    hasReplied.current = false;
    onToggle();
  };

  if (!isOpen) return null;

  return (
    <div className={Styles.panel}>
      <div className={Styles.header}>
        <span className={Styles.headerTitle}>{'Hire Digital Support'}</span>
        <button className={Styles.closeButton} onClick={handleClose}>
          <IconClose />
        </button>
      </div>

      <div className={Styles.messages}>
        {messages.map((msg, i) => (
          <div
            key={i}
            className={
              msg.from === 'user'
                ? `${Styles.bubble} ${Styles.bubbleUser}`
                : `${Styles.bubble} ${Styles.bubbleSupport}`
            }>
            {msg.text}
          </div>
        ))}
        {isTyping && (
          <div className={Styles.typing}>
            <span />
            <span />
            <span />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className={Styles.inputArea}>
        <textarea
          ref={textareaRef}
          className={Styles.textarea}
          placeholder='Type a message…'
          value={input}
          rows={1}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          className={Styles.sendButton}
          onClick={handleSend}
          disabled={!input.trim() || isSending}
          aria-label='Send'>
          <IconSend />
        </button>
      </div>
    </div>
  );
};

export default SupportModal;
